/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import "../../assets/styles/custom-styles.css";
import Form from "../../components/forms/Form";
import useForm from "../../hooks/useForm";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../utils/objectUtils";
import { roleEditFields } from "../../constants/forms/roleFields";
import useBaseService from "../../hooks/services/useBaseService";
import { entities } from "../../constants/entities";
import PageHeader from "../../components/PageHeader";
import SectionHeader from "../../components/forms/SectionHeader";
import SimpleBody from "../../components/forms/SimpleBody";
import { useMutation } from "@tanstack/react-query";
import useRemoteSelectOptions from "../../hooks/useRemoteSelectOptions";
import useLoader from "../../hooks/useLoader";
import { transformFiltersForUrl } from "../../utils/transformFiltersForUrl";
import { Box, Chip } from "@mui/material";
import useQueryWithLoader from "../../hooks/useQueryWithLoader";
import useNotification from "../../hooks/useNotification";

// 🆕 Helper: converte "objeto com chaves numéricas" em array de verdade
// (o useRemoteSelectOptions às vezes retorna {0: x, 1: y, ...} em vez de [x, y])
const toArray = (v) => (Array.isArray(v) ? v : Object.values(v || {}));

const EditRolePage = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const { formData, setFormData, handleChange } = useForm(
    setDefaultFieldValues([]),
  );
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();

  // 🆕 Guarda as permissões originais pra comparar no submit
  const originalPermissionsRef = useRef([]);

  const {
    get: baseServiceGet,
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
  } = useBaseService(navigate);

  const { data: roleResponseData, isLoading: isLoadingRole } =
    useQueryWithLoader({
      queryKey: ["role", roleId],
      queryFn: async () => {
        const response = await baseServiceGetByColumn(
          entities.roles.getById(roleId),
        );
        return response.result;
      },
      enabled: !!roleId,
      refetchOnWindowFocus: true,
    });

  const { data: rolePermissionData, isLoading: isLoadingRolePermission } =
    useQueryWithLoader({
      queryKey: ["rolePermission", roleId],
      queryFn: async () => {
        const response = await baseServiceGetByColumn(
          entities.roles.permissions(roleId),
        );
        return response.result;
      },
      enabled: !!roleId,
      refetchOnWindowFocus: true,
    });

  useEffect(() => {
    if (
      !isLoadingRole &&
      roleResponseData &&
      !isLoadingRolePermission &&
      rolePermissionData
    ) {
      hideLoader();
      const perms = toArray(
        rolePermissionData?.map((permission) => ({
          value: permission.id,
          label: permission.name,
        })),
      );

      originalPermissionsRef.current = perms.map((p) => p.value);
      setFormData({
        name: roleResponseData?.name,
        permissions: perms,
      });
    }

    if (isLoadingRole && isLoadingRolePermission) {
      showLoader();
    }
  }, [
    roleResponseData,
    rolePermissionData,
    isLoadingRole,
    isLoadingRolePermission,
  ]);

  const permissionSelect = useRemoteSelectOptions({
    queryKey: ["permissions"],
    fetcher: async ({ page = 1, perPage = 500, search = "" }) => {
      const filters = transformFiltersForUrl({
        ...(search && { name: search }),
      });
      const params = { filters, page, perPage };
      const response = await baseServiceGet(entities.permissions.get, params);
      return response.result;
    },
    fieldId: "permissions",
    formData,
    isMulti: true,
    mapToOption: (permission) => ({
      value: permission.id,
      label: permission.name,
    }),
    setFormData,
    perPage: 500,
    initialPage: 1,
    enablePagination: true,
  });

  const selects = {
    permissions: permissionSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};
      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects],
  );

  // 🆕 Normaliza o valor do select pra sempre ser array
  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      const sv = selectConfig?.selectedValue;
      return sv ? toArray(sv) : null;
    },
    [selects],
  );

  // 🆕 Wrapper que garante que o permissions no formData sempre seja array
  const handleFieldChange = useCallback(
    (fieldId, value) => {
      if (fieldId === "permissions") {
        setFormData((prev) => ({ ...prev, permissions: toArray(value) }));
        return;
      }
      handleChange(fieldId, value);
    },
    [handleChange, setFormData],
  );

  const updateRoleMutation = useMutation({
    mutationFn: (data) =>
      baseServicePut(entities.roles.getById(roleId), { name: data.name }),
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: (permissionIds) =>
      baseServicePut(entities.roles.permissionsUpdate(roleId), {
        permissions: permissionIds,
      }),
  });

  const handleSubmit = async () => {
    try {
      showLoader();

      // 1. Salva o nome
      await updateRoleMutation.mutateAsync(formData);

      // 2. Só sincroniza permissões se mudou (evita erro de "required" em cargos vazios)
      const currentIds = toArray(formData.permissions).map((p) => p.value);
      const originalIds = originalPermissionsRef.current;
      const changed =
        currentIds.length !== originalIds.length ||
        currentIds.some((id) => !originalIds.includes(id));

      if (changed) {
        await updatePermissionsMutation.mutateAsync(currentIds);
      }

      hideLoader();
      showNotification(
        "success",
        "Cargo atualizado com sucesso! Usuários deste cargo precisam relogar para aplicar as mudanças.",
      );
      navigate("/aluguel/cargos");
    } catch (error) {
      hideLoader();
      console.error("Erro ao salvar cargo:", error);
    }
  };

  const handleBack = () => navigate("/aluguel/cargos/");

  // 🆕 Array normalizado pra renderizar os Chips sem crash
  const permissionsList = toArray(formData?.permissions);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Cargo"
        showBackButton={true}
        backUrl="/aluguel/cargos"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Editar"
          textLoadingSubmit="Editando..."
          handleBack={handleBack}
        >
          {() => {
            const renderedSections = new Set();

            return roleEditFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleFieldChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "start",
                      flexWrap: "wrap",
                      mt: 2,
                    }}
                  >
                    {permissionsList.length > 0 &&
                      permissionsList.map((permission) => (
                        <Chip
                          key={permission.value}
                          label={String(permission.label ?? permission.value)}
                          sx={{ margin: "2px" }}
                        />
                      ))}
                  </Box>
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditRolePage;